import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { AccountState, type Account } from '@/services/auth/types';
import { DeliverySettings, useAccountEmailSetup, useListWorkspaces, useSampleWorkspace } from '@/features/workspaces';
import { isEmptyString } from '@/utils/string';

export type EssentialsOnboardingGate = {
  /** The onboarding cohort: trial, non-builder, flag on. */
  isEligible: boolean;
  isComplete: boolean;
  isLoading: boolean;
};

/** Minimal workspace projection for the any-workspace "live" check. */
type WorkspaceDeliveryProjection = {
  cio_workspace_id: number;
  delivery_settings: DeliverySettings;
};

export type EssentialsOnboardingCompletionInput = {
  hasVerifiedSendingDomain: boolean;
  workspaces: ReadonlyArray<WorkspaceDeliveryProjection>;
  /** Identifies the injected sample workspace (excluded — hardcoded to `normal`). */
  isSampleWorkspace: (workspaceId: number | null | undefined) => boolean;
};

/**
 * Whether a workspace is out of test mode (may send to its audience). Mirrors
 * the canonical test-mode gate in `delivery-mode-banner` and the workspace-setup
 * reconciler: test mode is `delivery_settings === SendToOne`; `Off` ("don't
 * send messages") is a separate delivery-disabled state, not test mode, and
 * counts as out of test mode there. An empty string is NOT out of test mode —
 * the wire type is `string`, so `''` (the factory default) survives the
 * `?? Normal` coalesce in `getMergedWorkspaceFromRaw` and would otherwise read
 * as live. Null/undefined get the same treatment (defensive — the coalesce
 * upstream already covers the real wire shape).
 */
export function isOutOfTestMode(deliverySettings: string | null | undefined): boolean {
  if (deliverySettings == null || isEmptyString(deliverySettings)) {
    return false;
  }
  return deliverySettings !== DeliverySettings.SendToOne;
}

/**
 * Onboarding is complete when the account has a verified sending domain AND
 * any non-sample workspace is out of test mode. Known imprecision (verified
 * domain in workspace A + live workspace B) is acceptable at trial scale.
 */
export function isEssentialsOnboardingComplete(input: EssentialsOnboardingCompletionInput): boolean {
  if (!input.hasVerifiedSendingDomain) {
    return false;
  }
  return input.workspaces.some(
    (w) => !input.isSampleWorkspace(w.cio_workspace_id) && isOutOfTestMode(w.delivery_settings),
  );
}

/**
 * The cohort answer without the two queries `useEssentialsOnboardingGate` runs
 * for `isComplete`. Takes the account rather than using `useAuth`, which throws
 * in dev on an empty store — wrong for layout chrome that tolerates a null one.
 */
export function useIsEssentialsOnboardingEligible(account: OnboardingCohortAccount | null | undefined): boolean {
  const isFlagEnabled = useFeatureFlag(AccountFeatureFlag.EssentialsOnboarding);

  if (account == null) {
    return false;
  }

  return isEligibleForOnboarding({ isFlagEnabled: isFlagEnabled === true, account });
}

/**
 * Single cohort decision for the essentials onboarding takeover. While
 * `isLoading` is true, `isEligible`/`isComplete` are provisional — callers
 * must not act on them until it settles.
 *
 * Composes `useAccountEmailSetup` (verified domain) + `useListWorkspaces` (any
 * non-sample workspace out of test mode). Both queries are gated on the
 * synchronously computed cohort rule so ineligible accounts fire zero requests.
 *
 * `enabled: false` suppresses the fetch, not the read: an ineligible account
 * whose cache was warmed by another caller (e.g. the authenticated layout
 * mounts `useListWorkspaces` for every account) gets `isComplete: true` out of
 * this hook. Consumers must guard on `isEligible` before acting on
 * `isComplete` — `selectHomeScreen` and `selectOnboardingRedirect` do.
 *
 * A terminal query error fails open: the hook settles with `isComplete: true`
 * and `isLoading: false` rather than holding forever. Holding would strand the
 * user on a blank home (`selectHomeScreen` → 'loading' → `null`) or a frozen
 * onboarding loader with no recovery (`refetchOnWindowFocus` is off and 4xx
 * never retries). Failing open hands the eligible cohort back to the standard
 * home / redirects off `/onboarding/*` — a usable, recoverable screen — instead
 * of a dead end. The decision is deliberately "do not take over on a read we
 * can't trust," not "regress to the checklist."
 */
export function useEssentialsOnboardingGate(): EssentialsOnboardingGate {
  const isFlagEnabled = useFeatureFlag(AccountFeatureFlag.EssentialsOnboarding);
  const { account } = useAuth();

  const isEligible = isEligibleForOnboarding({ isFlagEnabled: isFlagEnabled === true, account });

  return { isEligible, ...useEssentialsOnboardingCompletionState(isEligible) };
}

/** The gate's fetching half, shared by both entry points. */
type EssentialsOnboardingCompletionState = Omit<EssentialsOnboardingGate, 'isEligible'>;

function useEssentialsOnboardingCompletionState(isEligible: boolean): EssentialsOnboardingCompletionState {
  const emailSetup = useAccountEmailSetup({ enabled: isEligible });
  const { isSampleWorkspace } = useSampleWorkspace();
  const workspaces = useListWorkspaces({
    enabled: isEligible,
    select: (data): WorkspaceDeliveryProjection[] =>
      data.map((w) => ({ cio_workspace_id: w.cio_workspace_id, delivery_settings: w.delivery_settings })),
  });

  const isQueryError = emailSetup.isError || workspaces.isError;
  const hasVerifiedSendingDomain = emailSetup.data?.hasVerifiedSendingDomain ?? false;
  const isComplete = isQueryError
    ? true
    : isEssentialsOnboardingComplete({
        hasVerifiedSendingDomain,
        workspaces: workspaces.data ?? [],
        isSampleWorkspace,
      });

  return { isComplete, isLoading: isEligible && (emailSetup.isLoading || workspaces.isLoading) };
}

/**
 * Whether home would render the onboarding checklist for this account — the same
 * question `selectHomeScreen` answers, for a caller that needs to link there.
 *
 * False while the gate is unsettled and false on the documented fail-open, so a
 * caller never promises the checklist before knowing it is there. Takes the
 * account for the same reason `useIsEssentialsOnboardingEligible` does.
 */
export function useIsOnboardingChecklistVisible(account: OnboardingCohortAccount | null | undefined): boolean {
  const isEligible = useIsEssentialsOnboardingEligible(account);
  const { isComplete, isLoading } = useEssentialsOnboardingCompletionState(isEligible);

  return isEligible && !isComplete && !isLoading;
}

/** The account fields the cohort rule reads; both the raw payload and the augmented account satisfy it. */
export type OnboardingCohortAccount = Pick<Account, 'account_state' | 'plan_slug' | 'created_at'>;

export type EssentialsOnboardingEligibilityInput = {
  isFlagEnabled: boolean;
  account: OnboardingCohortAccount;
  /** Milliseconds since the epoch. Defaults to now; tests pass it so the rule stays pure. */
  nowMs?: number;
};

/**
 * How long a paid promo account stays in the cohort after it is created. A
 * promo signup pays before it reaches the wizard, so its account turns
 * `current` while every setup step is still ahead of it; the window keeps the
 * wizard and checklist available for that stretch, and completion still ends
 * the takeover earlier. Bound by account creation rather than promo
 * redemption recency: redemption isn't signup-only (an established customer
 * can redeem an unrelated code from billing), so keying on it would readmit
 * that customer to the cohort. Sized for a DNS hand-off to someone else, not
 * for the pass duration.
 */
export const PROMO_ONBOARDING_WINDOW_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whether the account was created within the window. `created_at` is unix
 * seconds on the wire; the comparison is in milliseconds.
 */
export function isWithinPromoOnboardingWindow(createdAtSeconds: number, nowMs: number): boolean {
  return nowMs - createdAtSeconds * 1000 <= PROMO_ONBOARDING_WINDOW_DAYS * DAY_MS;
}

/**
 * Who gets onboarding at all, with the flag on and a non-builder plan: a trial
 * account, or a paid account still inside its post-creation window above (a
 * promo checkout account that paid before reaching the wizard). Deliberately
 * says nothing about the signup answers — the wizard exists to ask what
 * someone wants to do, so gating it on having already said would be
 * circular.
 */
export function isEligibleForOnboarding(input: EssentialsOnboardingEligibilityInput): boolean {
  const { isFlagEnabled, account, nowMs = Date.now() } = input;
  if (!isFlagEnabled || isBuilderPlan(account)) {
    return false;
  }
  if (account.account_state === AccountState.Free) {
    return true;
  }
  return account.account_state === AccountState.Current && isWithinPromoOnboardingWindow(account.created_at, nowMs);
}
